import {
  CHAIN, ATOMIC_API, CONTRACT_ACCOUNT, COLLECTION, TEMPLATE_IDS,
  REWARD_SYMBOL_CODE, REWARD_PRECISION,
} from '../config/redeem';

/* Read-only chain + AtomicAssets helpers (plain fetch, no wallet needed). */

async function getTableRows(params) {
  // NOTE: no 'Content-Type: application/json' header — that would trigger a
  // CORS preflight that some WAX nodes (incl. greymass) don't answer. Antelope
  // parses the JSON body regardless, so a "simple" request works everywhere.
  const res = await fetch(`${CHAIN.url}/v1/chain/get_table_rows`, {
    method: 'POST',
    body: JSON.stringify({ json: true, limit: 1000, ...params }),
  });
  if (!res.ok) throw new Error(`get_table_rows failed (${res.status})`);
  const data = await res.json();
  return data.rows || [];
}

/* "12.34500000 KEK" -> { amount: 12.345, symbol: "KEK" } */
export function parseAsset(str) {
  if (!str) return { amount: 0, symbol: REWARD_SYMBOL_CODE };
  const [amt, symbol] = String(str).trim().split(' ');
  return { amount: parseFloat(amt) || 0, symbol: symbol || REWARD_SYMBOL_CODE };
}

export function formatKek(amount) {
  return `${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: REWARD_PRECISION,
  })} ${REWARD_SYMBOL_CODE}`;
}

/* Pending, claimable KEK balance for an account (accounts table, scope = owner). */
export async function fetchClaimable(actor) {
  const rows = await getTableRows({
    code: CONTRACT_ACCOUNT,
    scope: String(actor),
    table: 'accounts',
    limit: 20,
  });
  const row = rows.find((r) => parseAsset(r.balance).symbol === REWARD_SYMBOL_CODE);
  return row ? parseAsset(row.balance).amount : 0;
}

/* Map of template_id -> reward amount (rewards table, scope = contract). */
export async function fetchRewardTable() {
  const rows = await getTableRows({
    code: CONTRACT_ACCOUNT,
    scope: CONTRACT_ACCOUNT,
    table: 'rewards',
    limit: 1000,
  });
  const map = {};
  for (const r of rows) {
    if (r.collection_name === COLLECTION) {
      map[Number(r.template_id)] = parseAsset(r.reward).amount;
    }
  }
  return map;
}

/* Eligible NFTs an account owns (collection + whitelisted templates). */
export async function fetchEligibleNfts(actor) {
  const url = new URL(`${ATOMIC_API}/atomicassets/v1/assets`);
  url.searchParams.set('owner', String(actor));
  url.searchParams.set('collection_name', COLLECTION);
  url.searchParams.set('template_whitelist', TEMPLATE_IDS.join(','));
  url.searchParams.set('page', '1');
  url.searchParams.set('limit', '200');
  url.searchParams.set('order', 'desc');
  url.searchParams.set('sort', 'asset_id');

  const res = await fetch(url);
  if (!res.ok) throw new Error(`AtomicAssets query failed (${res.status})`);
  const { data } = await res.json();

  return (data || []).map((a) => ({
    assetId: a.asset_id,
    templateId: Number(a.template?.template_id ?? a.template_id),
    name: a.name || a.data?.name || `#${a.asset_id}`,
    img: resolveImg(a.data?.img || a.template?.immutable_data?.img),
  }));
}

function resolveImg(img) {
  if (!img) return null;
  if (/^https?:\/\//.test(img)) return img;
  return `https://ipfs.io/ipfs/${img}`;
}
